import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Building2, Search, Plus } from "lucide-react";
import { analytics } from "../../../lib/analytics";

interface Hotel {
  id: string;
  hotel_name: string;
  city: string;
  country: string;
  registration_number?: string; // Make optional since it's not always available
  connector_code?: string;
  connector_scopes?: string[];
  shared_scopes?: string[];
  status: "active" | "inactive" | "pending" | "requested" | "available";
  created_at?: string;
  scopes?: string[];
  pms_name: string; // PMS name from connectors table or connector code
  hotel_code?: string; // Hotel code from integrated_hotels view
  webhooks_enabled?: boolean; // Whether webhooks are enabled for this hotel
  integration_id?: string; // Integration ID for updating webhooks
}

export function ConnectedHotels() {
  const [connectedHotels, setConnectedHotels] = useState<Hotel[]>([]);
  const [searchResults, setSearchResults] = useState<Hotel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [requestingHotel, setRequestingHotel] = useState<string | null>(null);
  const [scopeMap, setScopeMap] = useState<Map<string, string>>(new Map());
  const [scopeNameToCodeMap, setScopeNameToCodeMap] = useState<Map<string, string>>(new Map());
  const [scopeCodeToNameMap, setScopeCodeToNameMap] = useState<Map<string, string>>(new Map());
  const [togglingWebhook, setTogglingWebhook] = useState<string | null>(null);

  useEffect(() => {
    // Load data when component mounts
    loadConnectedHotels();

    // Set up an interval to refresh data every 5 seconds while the component is mounted
    const intervalId = setInterval(() => {
      loadConnectedHotels();
    }, 5000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, []);

  // Helper function to fetch scope codes and their names
  const loadScopeMap = async () => {
    try {
      const { data: scopesData, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("code, name")
        .order("name");

      if (scopesError) throw scopesError;

      // Create a mapping of scope codes to names
      const newScopeMap = new Map();
      // Create a mapping of normalized scope names to codes (for webhook matching)
      const newNameToCodeMap = new Map();
      // Create a mapping of scope codes to normalized names (for trigger_objects conversion)
      const newCodeToNameMap = new Map();
      
      (scopesData || []).forEach((scope) => {
        // Use code as the key for the mapping
        newScopeMap.set(scope.code, scope.name);
        // Create normalized name (lowercase, replace spaces with underscores) -> code mapping
        const normalizedName = scope.name.toLowerCase().replace(/\s+/g, "_");
        newNameToCodeMap.set(normalizedName, scope.code);
        // Create code -> normalized name mapping
        newCodeToNameMap.set(scope.code, normalizedName);
      });

      setScopeMap(newScopeMap);
      setScopeNameToCodeMap(newNameToCodeMap);
      setScopeCodeToNameMap(newCodeToNameMap);
      return { scopeMap: newScopeMap, nameToCodeMap: newNameToCodeMap, codeToNameMap: newCodeToNameMap };
    } catch (err) {
      console.error("Error loading scope mappings:", err);
      setError("Failed to load scope information");
      return { scopeMap: new Map(), nameToCodeMap: new Map(), codeToNameMap: new Map() }; // Return empty maps in case of error
    }
  };

  const loadConnectedHotels = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Ensure scope map is loaded
      let currentScopeMap = scopeMap;
      if (scopeMap.size === 0) {
        const scopeMaps = await loadScopeMap();
        currentScopeMap = scopeMaps.scopeMap;
      }

      // Get hotels from integrated_hotels view
      const { data: integrationsData, error: integrationsError } =
        await supabase.from("integrated_hotels").select("*");

      if (integrationsError) throw integrationsError;

      // Get scopes for each hotel directly from the integrations table
      // since the view doesn't include the scopes we need
      const hotelIds = (integrationsData || [])
        .map((hotel) => hotel.hotel_id)
        .filter(Boolean);

      // If we have hotel IDs, fetch their scopes and webhooks
      let hotelScopesMap = new Map();
      let hotelWebhooksMap = new Map();
      let hotelIntegrationIdMap = new Map();
      if (hotelIds.length > 0) {
        try {
          // Get the scopes, webhooks, and id directly from the integrations table
          const { data: integrationsWithScopes, error: scopesError } =
            await supabase
              .from("integrations")
              .select("hotel_id, scopes, webhooks, id")
              .in("hotel_id", hotelIds)
              .eq("saas_id", user.id);

          if (scopesError) throw scopesError;

          // Create maps of hotel ID to scopes, webhooks, and integration_id
          (integrationsWithScopes || []).forEach((integration) => {
            hotelScopesMap.set(integration.hotel_id, integration.scopes || []);
            hotelWebhooksMap.set(integration.hotel_id, integration.webhooks);
            hotelIntegrationIdMap.set(integration.hotel_id, integration.id);
          });
        } catch (err) {
          console.error("Error fetching hotel scopes:", err);
        }
      }

      // Get all unique connector codes to fetch their names
      const connectorCodes = (integrationsData || [])
        .filter((hotel) => hotel.connector_code) // Get all hotels with a connector code
        .map((hotel) => hotel.connector_code.toLowerCase())
        .filter((code, index, self) => self.indexOf(code) === index); // Get unique codes

      // Fetch connector names from connectors table
      let connectorNameMap: Record<string, string> = {};
      if (connectorCodes.length > 0) {
        try {
          const { data: connectorsData, error: connectorsError } =
            await supabase
              .from("connectors")
              .select("code, name")
              .in("code", connectorCodes);

          if (connectorsError) {
            throw connectorsError;
          }

          // Create a map of connector code to name for quick lookup
          connectorNameMap = (connectorsData || []).reduce((map, connector) => {
            map[connector.code.toLowerCase()] = connector.name;
            return map;
          }, {} as Record<string, string>);
        } catch (err) {
          console.error("Error fetching connector names:", err);
        }
      }

      // Filter out potential duplicates based on hotel_id to ensure unique entries
      const uniqueIntegrationsData = (integrationsData || []).reduce((acc: any[], hotel: any) => {
        const exists = acc.find((h) => h.hotel_id === hotel.hotel_id);
        if (!exists) {
          acc.push(hotel);
        }
        return acc;
      }, []);

      const hotelsMapped: Hotel[] = uniqueIntegrationsData.map((hotel) => {
        // Get the connector scopes for this hotel
        const hotelScopes = hotelScopesMap.get(hotel.hotel_id) || [];

        // Map the scope codes to names
        const scopeNames = hotelScopes
          .map(
            (scopeCode: string) => currentScopeMap.get(scopeCode) || scopeCode
          )
          .filter(Boolean);

        // Get connector name from the map if available
        const connectorCode = hotel.connector_code?.toLowerCase();

        const connectorName = connectorCode
          ? connectorNameMap[connectorCode]
          : null;

        // Get webhooks and integration_id for this hotel
        const webhooks = hotelWebhooksMap.get(hotel.hotel_id);
        const webhooks_enabled = webhooks !== null && webhooks !== undefined && 
          Array.isArray(webhooks) && webhooks.length > 0;
        const integration_id = hotelIntegrationIdMap.get(hotel.hotel_id);

        const mappedHotel = {
          id: hotel.hotel_id,
          hotel_name: hotel.hotel_name,
          city: hotel.city,
          country: hotel.country,
          registration_number: hotel.registration_number,
          pms_name: connectorName || hotel.connector_code || "Unknown",
          status: hotel.status as Hotel["status"],
          created_at: hotel.created_at,
          scopes: scopeNames,
          hotel_code: hotel.hotel_code || "N/A", // Include hotel_code from integrated_hotels view
          webhooks_enabled,
          integration_id,
          connector_code: hotel.connector_code,
        };

        return mappedHotel;
      });
      console.log("Mapped hotels:", hotelsMapped);
      setConnectedHotels(hotelsMapped);
    } catch (err) {
      setError("Failed to load connected hotels");
      console.error("Error loading hotels:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookToggle = async (hotelId: string, integrationId: string | undefined, enabled: boolean) => {
    if (!integrationId) {
      setError("Integration ID not found for this hotel");
      return;
    }

    try {
      setTogglingWebhook(hotelId);
      setError("");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      console.log("Toggling webhooks:", { hotelId, integrationId, enabled, userId: user.id });

      if (enabled) {
        // Get saas_details.webhooks
        const { data: saasDetails, error: saasError } = await supabase
          .from("saas_details")
          .select("webhooks")
          .eq("id", user.id)
          .single();

        if (saasError) {
          console.error("Error fetching saas_details:", saasError);
          throw saasError;
        }

        console.log("SaaS webhooks from database:", saasDetails?.webhooks);

        const saasWebhooks = saasDetails?.webhooks || [];
        if (!Array.isArray(saasWebhooks) || saasWebhooks.length === 0) {
          setError("No webhooks configured. Please configure webhooks first.");
          await loadConnectedHotels(); // Reload to reset toggle state
          return;
        }

        // Get hotel's connector code
        const hotel = connectedHotels.find((h) => h.id === hotelId);
        if (!hotel?.connector_code) {
          setError("Hotel connector not found");
          await loadConnectedHotels(); // Reload to reset toggle state
          return;
        }

        console.log("Hotel connector code:", hotel.connector_code);

        // Get connector's trigger_objects
        const { data: connector, error: connectorError } = await supabase
          .from("connectors")
          .select("trigger_objects")
          .eq("code", hotel.connector_code)
          .single();

        if (connectorError) {
          console.error("Error fetching connector:", connectorError);
          throw connectorError;
        }

        const triggerObjects = connector?.trigger_objects || [];
        console.log("Connector trigger_objects (scope codes):", triggerObjects);
        console.log("SaaS webhooks:", saasWebhooks);

        // Load scope mappings if not already loaded
        let currentCodeToNameMap = scopeCodeToNameMap;
        if (currentCodeToNameMap.size === 0) {
          const scopeMaps = await loadScopeMap();
          currentCodeToNameMap = scopeMaps.codeToNameMap;
        }
        console.log("Scope code to normalized name map:", Array.from(currentCodeToNameMap.entries()));

        // Convert trigger_objects from scope codes to normalized names for comparison
        // trigger_objects are stored as scope codes (e.g., "reservations")
        // webhooks are stored as normalized names (e.g., "reservations")
        // First, reload all scopes to ensure we have the latest mapping
        const { data: allScopesData, error: allScopesError } = await supabase
          .from("pms_scopes")
          .select("code, name");
        
        // Build a fresh mapping from the database
        const freshCodeToNameMap = new Map<string, string>();
        if (!allScopesError && allScopesData) {
          allScopesData.forEach((scope) => {
            const normalizedName = scope.name.toLowerCase().replace(/\s+/g, "_");
            if (scope.code) {
              freshCodeToNameMap.set(scope.code, normalizedName);
            }
            // Also map the normalized name itself (in case code is missing or same as normalized)
            freshCodeToNameMap.set(normalizedName, normalizedName);
          });
        }
        
        // Standardization mapping: convert old scope codes to new standardized names
        // This aligns with backend standardization: booking -> reservations, customer -> guests
        const standardizationMap: Record<string, string> = {
          "booking": "reservations",
          "bookings": "reservations",
          "customer": "guests",
          "customer_data": "guests",
          "customers": "guests",
        };
        
        const triggerObjectsNormalized = triggerObjects.map((code: string) => {
          // First try standardization map (for old codes)
          if (standardizationMap[code]) {
            console.log(`Converting trigger_object code "${code}" via standardization map to: "${standardizationMap[code]}"`);
            return standardizationMap[code];
          }
          // Then try fresh map, then fallback to cached map, then use code as-is
          let normalized = freshCodeToNameMap.get(code) || currentCodeToNameMap.get(code) || code;
          // Apply standardization to the normalized name as well (in case it's "bookings" -> "reservations")
          if (standardizationMap[normalized]) {
            normalized = standardizationMap[normalized];
          }
          console.log(`Converting trigger_object code "${code}" to normalized name: "${normalized}"`);
          return normalized;
        });
        console.log("Connector trigger_objects (normalized names):", triggerObjectsNormalized);

        // Filter webhooks to only include entries for objects that match trigger_objects
        // Webhooks are stored with normalized scope names (e.g., "reservations", "guests")
        // trigger_objects are stored as scope codes (e.g., "reservations", "guests")
        // So we convert trigger_objects to normalized names for comparison
        const filteredWebhooks = saasWebhooks.filter(
          (webhookEntry: Record<string, string>) => {
            const objectType = Object.keys(webhookEntry)[0]; // e.g., "reservations"
            // Check if the webhook object_type matches any normalized trigger_object
            const matches = triggerObjectsNormalized.includes(objectType);
            console.log(`Webhook object_type "${objectType}" matches any of [${triggerObjectsNormalized.join(", ")}]: ${matches}`);
            return matches;
          }
        );

        console.log("Filtered webhooks:", filteredWebhooks);

        // If no matching webhooks, show error and don't enable
        if (filteredWebhooks.length === 0) {
          setError(`No webhooks configured for this connector's trigger objects (${triggerObjects.join(", ") || "none"})`);
          await loadConnectedHotels(); // Reload to reset toggle state
          return;
        }

        // Verify the integration belongs to the current user and get current scopes
        const { data: verifyIntegration, error: verifyError } = await supabase
          .from("integrations")
          .select("id, saas_id, scopes")
          .eq("id", integrationId)
          .eq("saas_id", user.id)
          .single();

        if (verifyError || !verifyIntegration) {
          console.error("Integration verification failed:", verifyError);
          throw new Error("Integration not found or access denied");
        }

        // Get current scopes and add 'webhooks' if not already present
        const currentScopes = verifyIntegration.scopes || [];
        const updatedScopes = currentScopes.includes("webhooks")
          ? currentScopes
          : [...currentScopes, "webhooks"];

        console.log("Current scopes:", currentScopes);
        console.log("Updated scopes (with webhooks):", updatedScopes);

        // Update integrations.webhooks and integrations.scopes
        const { data: updateData, error: updateError } = await supabase
          .from("integrations")
          .update({ 
            webhooks: filteredWebhooks,
            scopes: updatedScopes
          })
          .eq("id", integrationId)
          .eq("saas_id", user.id)
          .select();

        if (updateError) {
          console.error("Error updating integrations.webhooks:", updateError);
          throw updateError;
        }

        if (!updateData || updateData.length === 0) {
          console.error("Update returned no data - RLS policy might be blocking");
          throw new Error("Update failed - no data returned");
        }

        console.log("Successfully updated integrations.webhooks:", updateData);

        // Verify the update persisted
        const { data: verifyUpdate, error: verifyUpdateError } = await supabase
          .from("integrations")
          .select("webhooks, scopes")
          .eq("id", integrationId)
          .eq("saas_id", user.id)
          .single();

        if (verifyUpdateError) {
          console.error("Error verifying update:", verifyUpdateError);
        } else {
          console.log("Verified webhooks after update:", verifyUpdate?.webhooks);
          console.log("Verified scopes after update:", verifyUpdate?.scopes);
        }
      } else {
        // Verify the integration belongs to the current user and get current scopes
        const { data: verifyIntegration, error: verifyError } = await supabase
          .from("integrations")
          .select("id, saas_id, scopes")
          .eq("id", integrationId)
          .eq("saas_id", user.id)
          .single();

        if (verifyError || !verifyIntegration) {
          console.error("Integration verification failed:", verifyError);
          throw new Error("Integration not found or access denied");
        }

        // Get current scopes and remove 'webhooks' if present
        const currentScopes = verifyIntegration.scopes || [];
        const updatedScopes = currentScopes.filter((scope: string) => scope.toLowerCase() !== "webhooks");

        console.log("Current scopes:", currentScopes);
        console.log("Updated scopes (without webhooks):", updatedScopes);

        // Disable webhooks - set to null and remove 'webhooks' from scopes
        const { data: updateData, error: updateError } = await supabase
          .from("integrations")
          .update({ 
            webhooks: null,
            scopes: updatedScopes
          })
          .eq("id", integrationId)
          .eq("saas_id", user.id)
          .select();

        if (updateError) {
          console.error("Error disabling webhooks:", updateError);
          throw updateError;
        }

        if (!updateData || updateData.length === 0) {
          console.error("Update returned no data - RLS policy might be blocking");
          throw new Error("Update failed - no data returned");
        }

        console.log("Successfully disabled webhooks:", updateData);

        // Verify the update persisted
        const { data: verifyUpdate, error: verifyUpdateError } = await supabase
          .from("integrations")
          .select("webhooks, scopes")
          .eq("id", integrationId)
          .eq("saas_id", user.id)
          .single();

        if (verifyUpdateError) {
          console.error("Error verifying update:", verifyUpdateError);
        } else {
          console.log("Verified webhooks after update:", verifyUpdate?.webhooks);
          console.log("Verified scopes after update:", verifyUpdate?.scopes);
        }
      }

      // Reload connected hotels to reflect the change
      await loadConnectedHotels();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to toggle webhooks";
      setError(errorMessage);
      console.error("Error toggling webhooks:", err);
      // Reload to reset toggle state on error
      await loadConnectedHotels();
    } finally {
      setTogglingWebhook(null);
    }
  };

  const searchHotels = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      // Ensure scope map is loaded
      let currentScopeMap = scopeMap;
      if (scopeMap.size === 0) {
        const scopeMaps = await loadScopeMap();
        currentScopeMap = scopeMaps.scopeMap;
      }

      // Search for hotels
      const { data, error: queryError } = await supabase
        .from("available_hotels")
        .select("*")
        .or(
          `hotel_name.ilike.%${searchTerm}%,registration_number.ilike.%${searchTerm}%`
        )
        .order("hotel_name");

      if (queryError) throw queryError;

      // Map the results to include scope names instead of codes
      const mappedData = (data || []).map((hotel) => {
        // Get the shared scopes from the hotel
        const scopeCodes = hotel.shared_scopes || [];

        // Map the scope codes to names
        const scopeNames = scopeCodes
          .map(
            (scopeCode: string) => currentScopeMap.get(scopeCode) || scopeCode
          )
          .filter(Boolean);

        return {
          ...hotel,
          scopes: scopeNames,
          hotel_code: hotel.hotel_code || "N/A",
        };
      });

      setSearchResults(mappedData);
      setError("");
    } catch (err) {
      // Only show error message if it's not related to empty results
      if (err instanceof Error && err.message.includes("no rows")) {
        setSearchResults([]);
        setError("");
      } else {
        setError("Failed to search hotels");
        console.error("Error searching hotels:", err);
      }
    }
  };

  const requestIntegration = async (hotelId: string) => {
    try {
      setRequestingHotel(hotelId);
      setError("");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Find the hotel in search results to get its shared scopes
      const hotel = searchResults.find((h) => h.id === hotelId);
      if (!hotel) throw new Error("Hotel not found in search results");

      // Get the shared scopes from the hotel
      const sharedScopes = hotel.shared_scopes || [];

      // Get SaaS company name for notification
      const { data: saasCompanyData } = await supabase
        .from("saas_details")
        .select("company_name")
        .eq("id", user.id)
        .single();

      const companyName = saasCompanyData?.company_name || "A SaaS provider";

      // Check if there's already an integration request
      const { data: existingIntegration, error: checkError } = await supabase
        .from("integrations")
        .select("id, status")
        .match({ hotel_id: hotelId, saas_id: user.id })
        .maybeSingle();

      if (checkError) throw checkError;

      let integrationId;

      if (existingIntegration) {
        if (existingIntegration.status === "requested") {
          setError("Integration request already pending for this hotel");
          return;
        }
        // Update existing integration to requested status with shared scopes
        const { error: updateError } = await supabase
          .from("integrations")
          .update({
            status: "requested",
            scopes: sharedScopes, // Save the shared scopes
          })
          .match({ id: existingIntegration.id });

        if (updateError) throw updateError;

        integrationId = existingIntegration.id;
      } else {
        // Create new integration request with shared scopes
        const { data: newIntegration, error: insertError } = await supabase
          .from("integrations")
          .insert([
            {
              hotel_id: hotelId,
              saas_id: user.id,
              status: "requested",
              scopes: sharedScopes, // Save the shared scopes
            },
          ])
          .select();

        if (insertError) throw insertError;

        integrationId = newIntegration?.[0]?.id;
      }

      // Create notification for the hotel
      if (integrationId) {
        const { error: notificationError } = await supabase
          .from("notifications")
          .insert({
            recipient_id: hotelId,
            sender_id: user.id,
            type: "integration_request",
            title: "New Integration Request",
            message: `${companyName} has requested to integrate with your hotel`,
            data: {
              integration_id: integrationId,
              saas_id: user.id,
              saas_name: companyName,
            },
          });

        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        }
      }

      // Track hotel connection request
      analytics.trackHotelConnectionRequest(hotelId, user.id);

      // Refresh the lists
      await loadConnectedHotels();
      await searchHotels();

      // Show success message
      setError("Integration request sent successfully");
      setTimeout(() => setError(""), 3000);
    } catch (err) {
      setError("Failed to request integration");
      console.error("Error requesting integration:", err);
    } finally {
      setRequestingHotel(null);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading hotels...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Building2 className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-600">
              {isSearching ? "Search Hotels" : "Connected Hotels"}
            </h2>
          </div>
          <button
            onClick={() => {
              setIsSearching(!isSearching);
              setSearchTerm("");
              setSearchResults([]);
              setError("");
            }}
            className="btn-outline-blue"
          >
            {isSearching ? "View Connected" : "Search Hotels"}
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div
            className={`mb-4 p-4 ${
              error.includes("successfully") ? "bg-green-50" : "bg-red-50"
            } text-sm rounded-md border ${
              error.includes("successfully")
                ? "border-green-200"
                : "border-red-200"
            }`}
          >
            {error}
          </div>
        )}

        {isSearching ? (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by hotel name or registration number"
                    className="form-input pl-10"
                  />
                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>
              <button
                onClick={searchHotels}
                className="btn-primary inline-flex items-center"
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </button>
            </div>

            {searchResults.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">Hotel Name</th>
                      <th className="table-header">Hotel Code</th>
                      <th className="table-header">Location</th>
                      <th className="table-header">Shared Scopes</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {searchResults.map((hotel) => (
                        <tr key={hotel.id} className="table-row">
                          <td className="table-cell">{hotel.hotel_name}</td>
                          <td className="table-cell">
                            {hotel.hotel_code || "N/A"}
                          </td>
                          <td className="table-cell">
                            {hotel.city}, {hotel.country}
                          </td>
                          <td className="table-cell">
                            {hotel.scopes && hotel.scopes.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {hotel.scopes.map((scope, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                                  >
                                    {scope}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                No shared scopes
                              </span>
                            )}
                          </td>
                          <td className="table-cell">
                            <span
                              className={`status-badge ${
                                hotel.status === "available"
                                  ? "status-active"
                                  : hotel.status === "requested"
                                  ? "status-warning"
                                  : "status-inactive"
                              }`}
                            >
                              {hotel.status === "available"
                                ? "Available"
                                : "Integration Requested"}
                            </span>
                          </td>
                          <td className="table-cell">
                            {hotel.status === "available" && (
                              <button
                                onClick={() => requestIntegration(hotel.id)}
                                disabled={requestingHotel === hotel.id}
                                className="btn-primary inline-flex items-center"
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                {requestingHotel === hotel.id
                                  ? "Requesting..."
                                  : "Request Integration"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              searchTerm && (
                <div className="text-center py-8">
                  <p className="px-6 py-4 text-center text-sm text-gray-500">
                    No hotels found matching your search criteria.
                  </p>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            {connectedHotels.length === 0 ? (
              <div className="text-center py-8">
                <p className="px-6 py-4 text-center text-sm text-gray-500">
                  No connected hotels found. Use the "Search Hotels" button to
                  find and connect to hotels.
                </p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Hotel Name</th>
                    <th className="table-header">Hotel Code</th>
                    <th className="table-header">Location</th>
                    <th className="table-header">PMS</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Scopes</th>
                    <th className="table-header">Webhooks</th>
                    <th className="table-header">Connected Since</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {connectedHotels.map((hotel, index) => (
                    <tr key={hotel.integration_id || `${hotel.id}-${hotel.hotel_code}-${index}`} className="table-row">
                      <td className="table-cell">{hotel.hotel_name}</td>
                      <td className="table-cell">{hotel.hotel_code}</td>
                      <td className="table-cell">
                        {hotel.city}, {hotel.country}
                      </td>
                      <td className="table-cell">{hotel.pms_name}</td>
                      <td className="table-cell">
                        <span
                          className={`status-badge ${
                            hotel.status === "active"
                              ? "status-active"
                              : hotel.status === "requested"
                              ? "status-warning"
                              : "status-inactive"
                          }`}
                        >
                          {hotel.status === "requested"
                            ? "Integration Requested"
                            : hotel.status.charAt(0).toUpperCase() +
                              hotel.status.slice(1)}
                        </span>
                      </td>
                      <td className="table-cell">
                        {hotel.scopes && hotel.scopes.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {hotel.scopes.map((scope, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                              >
                                {scope}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No shared scopes
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        {hotel.status === "active" && hotel.integration_id ? (
                          <button
                            onClick={() =>
                              handleWebhookToggle(
                                hotel.id,
                                hotel.integration_id,
                                !hotel.webhooks_enabled
                              )
                            }
                            disabled={togglingWebhook === hotel.id}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                              hotel.webhooks_enabled
                                ? "bg-indigo-600"
                                : "bg-gray-200"
                            }`}
                            title={
                              hotel.webhooks_enabled
                                ? "Disable webhooks"
                                : "Enable webhooks"
                            }
                          >
                            {togglingWebhook === hotel.id ? (
                              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-white">
                                ...
                              </span>
                            ) : (
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  hotel.webhooks_enabled
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="table-cell">
                        {hotel.created_at &&
                          new Date(hotel.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
